/**
 * ItemCommitmentsSection
 *
 * Shows which Commitment Registry entries this item is linked to.
 * Only rendered when the `commitment_registry` workspace module is enabled.
 *
 * Follows the same self-contained pattern as CustomFieldsSection — owns its
 * own store access and module gate so ItemDetailEditable stays lean.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, ExternalLink, CalendarDays } from 'lucide-react';
import { useCommitmentStore } from '../../../stores/prioritization/commitmentStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { getWorkspaceModules, isModuleEnabled } from '../../../lib/workspaceModulesService';
import type { CommitmentCoverage, CommitmentType, CommitmentStatus } from '../../../types/prioritization/commitment';

// ── Type / status display helpers ─────────────────────────────────────────────

const TYPE_LABELS: Record<CommitmentType, string> = {
  customer: 'Customer',
  partner: 'Partner',
  regulatory: 'Regulatory',
  internal: 'Internal',
};

const TYPE_COLORS: Record<CommitmentType, string> = {
  customer: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  partner: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  regulatory: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  internal: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
};

const STATUS_DOT: Record<CommitmentStatus, string> = {
  active: 'bg-[var(--green)]',
  fulfilled: 'bg-[var(--slate)]',
  expired: 'bg-[var(--surface)]',
  cancelled: 'bg-[var(--coral)]',
  suspended: 'bg-[var(--mustard)]',
  superseded: 'bg-[var(--slate)]',
};

const COVERAGE_CHIP: Record<CommitmentCoverage, string> = {
  covered: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  partial: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  'at-risk': 'bg-[var(--coral-dim)] text-[var(--coral)]',
  'no-items': 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
};

// eslint-disable-next-line react-refresh/only-export-components -- also read by the item page's Connections rows
export const COVERAGE_LABEL: Record<CommitmentCoverage, string> = {
  covered: 'Covered',
  partial: 'Partial',
  'at-risk': 'At Risk',
  'no-items': 'No Items',
};

function formatDate(dueDate: string | null): string | null {
  if (!dueDate) return null;
  const d = new Date(dueDate + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dueDate + 'T00:00:00') < today;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface ItemCommitmentsSectionProps {
  itemId: string;
}

export function ItemCommitmentsSection({ itemId }: ItemCommitmentsSectionProps) {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [moduleEnabled, setModuleEnabled] = useState(false);
  const [moduleChecked, setModuleChecked] = useState(false);

  const { fetchFromSupabase, getLinkedCommitmentsForItem, syncState } = useCommitmentStore();

  // ── Module gate ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    getWorkspaceModules(workspaceId).then(({ data }) => {
      if (!cancelled) {
        setModuleEnabled(isModuleEnabled(data, 'commitment_registry'));
        setModuleChecked(true);
      }
    });
    return () => { cancelled = true; };
  }, [workspaceId]);

  // ── Fetch commitments if module enabled ─────────────────────────────────────
  useEffect(() => {
    if (moduleEnabled && !syncState.isLoading) {
      fetchFromSupabase();
    }
  // We intentionally only re-run when moduleEnabled changes, not on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleEnabled]);

  if (!moduleChecked || !moduleEnabled) return null;

  const linked = getLinkedCommitmentsForItem(itemId);

  return (
    <div className="mt-4 border-t border-[var(--rule)] pt-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-5 h-5 rounded flex items-center justify-center bg-[var(--slate-dim)] flex-shrink-0">
          <Handshake className="h-3 w-3 text-[var(--text-ter)]" />
        </div>
        <span className="text-sm font-semibold text-[var(--text-pri)]">Commitments</span>
        <span className="text-xs text-[var(--text-ter)]">({linked.length})</span>
        <Link
          to="/prioritization/commitments"
          className="ml-auto text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] flex items-center gap-0.5"
        >
          <ExternalLink className="h-3 w-3" />
          Registry
        </Link>
      </div>

      {linked.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)] italic">
          This item is not linked to any commitments.{' '}
          <Link to="/prioritization/commitments" className="text-[var(--text-ter)] hover:underline">
            Open registry
          </Link>{' '}
          to link it.
        </p>
      ) : (
        <div className="space-y-1.5">
          {linked.map((c) => (
            <div
              key={c.id}
              className="border border-[var(--rule)] bg-[var(--surface)] px-2.5 py-2 flex items-start gap-2"
            >
              <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[c.status]}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Link
                    to={`/prioritization/commitments/${c.id}`}
                    className="text-xs font-medium text-[var(--text-pri)] hover:text-[var(--text-ter)] truncate"
                  >
                    {c.title}
                  </Link>
                </div>
                {c.counterparty && (
                  <div className="text-xs text-[var(--text-ter)] truncate">{c.counterparty}</div>
                )}
                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${TYPE_COLORS[c.commitmentType]}`}>
                    {TYPE_LABELS[c.commitmentType]}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${COVERAGE_CHIP[c.coverage]}`}>
                    {COVERAGE_LABEL[c.coverage]}
                  </span>
                  {c.dueDate && (
                    <span className={`text-xs flex items-center gap-0.5 ${isOverdue(c.dueDate) ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}`}>
                      <CalendarDays className="h-3 w-3" />
                      {formatDate(c.dueDate)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
